import { checkActiveMember } from '../functions/appHeader.js'
import { navItemVisible } from '../functions/featureFlags.js'
import { headerOverlayPatch } from './headerOverlayState.js'
import { activeHighlight } from './_activeHighlight.js'
import { touchFloor } from './_touchTarget.js'
import media from '@symbo-ls/brand/designSystem/media.js'

// Single source of truth for the chrome islands' pill gauge (thickness) —
// HeaderLeft, HeaderTabs and HeaderRight must read as one row of equal pills.
// Previously HeaderLeft and HeaderRight each hard-coded '2.26rem' while
// HeaderTabs pinned nothing at all, so the centre island rendered 1px shorter
// than its neighbours (measured 38 / 37 / 38 at an 830px viewport) — visible
// as soon as the viewport got tight enough to matter, e.g. iPad portrait.
// rem (root = 17px) keeps it scope-independent, matching the search pill's
// own fontSize-driven height. Exported: the same gauge is the tablet rail's
// minWidth below and the bottom composer's resting minHeight (canvas
// CanvasPromptTextarea), so every chrome island shares one thickness. A
// plain top-level string export (never read inside a factory function in
// THIS file) — safe as-is, unlike the imperative cluster below.
export const HEADER_ISLAND_MIN_HEIGHT = '2.26rem'

// AppHeader — one flat flex navbar (2026-06-08 per Nika "unwrap AppHeader
// items to be one flex navbar; remove legacy duplicates (search) and
// pagetitle"). The old structure nested everything in a MainNavbar
// sub-navbar (org switcher + search) and a UserActions sub-navbar (the
// right cluster), plus a duplicate Logo and two hidden legacy slots
// (PageTitle, a parked SearchBar). This is the de-nested version: every
// item is a DIRECT flex child of the header.
//
// Layout: Logo + org switcher cluster on the LEFT (the org switcher's
// `marginRight: auto` absorbs the free space and pushes the user-actions
// cluster to the RIGHT edge); the search pill is absolutely centered on
// top — same `width: H3` footprint and centering approach as the bottom
// CanvasPromptTextarea, so both read as one control family and the
// centering is independent of the left/right cluster widths.
//
// MainNavbar (the icon-row variant) + the standalone HeaderSearch stay
// intact for the canvas + analytics headers that still mount them.
export const AppHeader = {
  // FA204 / frank trap — every imperative responsive-sync helper below lives
  // in `scope`, not module scope: a bare module-scope const/function
  // silently reads as undefined once the component is frank.toJSON'd for
  // platform delivery (workspace d6ea1142,
  // project_frank_drops_module_local_helpers). Called via
  // `el.scope.<name>(...)` from any descendant factory (`scope:` merges onto
  // every instance). Regular `function` syntax (not arrows) throughout this
  // object — `el.scope.foo()` binds `this` to `el.scope`, so sibling helpers
  // call each other via `this.<name>(...)` instead of a bare identifier
  // (object-literal properties can't see each other as free variables).
  scope: {
    // Kept for the `onInit`/`watchMediaQueries` call sites below (those
    // resolve fine via `el.scope`). Query strings, evaluated once here —
    // plain values, not function bodies, so they bake to literal strings at
    // serialization time same as HEADER_ISLAND_MIN_HEIGHT above.
    MOBILE_HEADER_SEARCH_QUERY: media.mobileL,
    TABLET_HEADER_SEARCH_QUERY: media.tabletS,
    TABLET_MIDDLE_MAX_WIDTH:
      'calc(100% - var(--hdr-left-w) - var(--hdr-right-w) - 2 * (var(--spacing-X2) + var(--spacing-A)))',
    TABLET_MIDDLE_LEFT: 'calc(var(--hdr-left-w) + var(--spacing-X2) + var(--spacing-A))',

    clearInlineStyles(node, properties) {
      if (!node) return
      for (const property of properties) node.style.removeProperty(property)
    },

    // The tablet writers below own the TOP navbar only: on a rail or at the
    // bottom (functions/navbarLayout.js) they clear what they wrote and stand
    // aside — the header's own case blocks lay it out there.
    navAtTop(el) {
      const root = el && el.state && el.state.root
      const nav = el && typeof el.call === 'function' ? el.call('navbarLayoutRead', root) : null
      return !nav || nav.position === 'top'
    },

    applyNowAndNextFrame(apply) {
      apply()
      window.requestAnimationFrame?.(apply)
    },

    watchMediaQueries(el, key, queries, sync) {
      if (typeof window === 'undefined' || el[key]) return
      const mediaQueries = queries.map((query) => window.matchMedia?.(query)).filter(Boolean)
      if (!mediaQueries.length) return
      const handler = () => sync()
      for (const query of mediaQueries) {
        if (query.addEventListener) query.addEventListener('change', handler)
        else if (query.addListener) query.addListener(handler)
      }
      el[key] = { mediaQueries, handler }
    },

    unwatchMediaQueries(el, key) {
      const watcher = el?.[key]
      if (!watcher) return
      for (const query of watcher.mediaQueries) {
        if (query.removeEventListener) query.removeEventListener('change', watcher.handler)
        else if (query.removeListener) query.removeListener(watcher.handler)
      }
      el[key] = null
    },

    clearHeaderLeftCompactStyle(node) {
      if (!node) return
      this.clearInlineStyles(node, [
        'width',
        'min-height',
        'max-height',
        'padding',
        'gap',
        'flex-direction',
        'border-radius',
        'box-sizing',
        'align-items',
        'justify-content',
        'position'
      ])
      const logo = node.querySelector(':scope > [data-key="Logo"]')
      this.clearInlineStyles(logo, [
        'position',
        'inset',
        'width',
        'height',
        'padding',
        'align-items',
        'justify-content'
      ])
      const actions = node.querySelector(':scope > [data-key="HeaderActions"]')
      this.clearInlineStyles(actions, [
        'opacity',
        'max-height',
        'transform',
        'pointer-events',
        'overflow',
        'display'
      ])
    },

    syncHeaderLeftCompactLayout(el, s) {
      if (typeof window === 'undefined' || !el?.node) return
      const compact =
        window.matchMedia?.(this.MOBILE_HEADER_SEARCH_QUERY).matches &&
        !!(s.root?._canvasSlot || s.root?._canvasSlotSecond)

      this.clearHeaderLeftCompactStyle(el.node)
      const doc = document.documentElement
      if (!compact) {
        delete doc.dataset.headerLeftCompact
        return
      }
      doc.dataset.headerLeftCompact = 'true'

      const logo = el.node.querySelector(':scope > [data-key="Logo"]')
      const actions = el.node.querySelector(':scope > [data-key="HeaderActions"]')
      // Keep origin/main's logo position, but constrain the compact surface
      // to an equal box so the base navbar cannot stretch into a horizontal
      // pill.
      el.node.style.setProperty('width', '2.5rem')
      el.node.style.setProperty('min-height', '2.5rem')
      el.node.style.setProperty('max-height', '2.5rem')
      el.node.style.setProperty('padding', '0')
      el.node.style.setProperty('gap', '0')
      el.node.style.setProperty('flex-direction', 'row')
      el.node.style.setProperty('border-radius', '50%')
      el.node.style.setProperty('box-sizing', 'border-box')
      el.node.style.setProperty('align-items', 'center')
      el.node.style.setProperty('justify-content', 'center')
      el.node.style.setProperty('position', 'relative')
      if (logo) {
        logo.style.setProperty('position', 'absolute')
        logo.style.setProperty('inset', '0')
        logo.style.setProperty('width', '100%')
        logo.style.setProperty('height', '100%')
        logo.style.setProperty('padding', '0')
        logo.style.setProperty('align-items', 'center')
        logo.style.setProperty('justify-content', 'center')
      }
      if (!actions) return
      actions.style.setProperty('display', 'none')
      actions.style.setProperty('pointer-events', 'none')
    },

    // Only what the tablet lane below writes inline. NOT z-index
    // (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1): nothing here sets it since the
    // phone path's inline `z-index: 20` left (workspace f4c5b736d), and the
    // framework lands the open pill's own state (`.isSearchOpen`: opacity,
    // pointer events, z) on the node's style — clearing z-index on every
    // update dropped the open pill back under the header islands on a phone
    // (measured on dev d2803c9e6: open, opacity 1, z auto).
    clearHeaderSearchResponsiveStyle(node) {
      this.clearInlineStyles(node, [
        'left',
        'right',
        'top',
        'width',
        'max-width',
        'min-width',
        'transform'
      ])
    },

    syncHeaderSearchResponsiveLayout(el, s) {
      if (typeof window === 'undefined' || !el?.node) return
      const open = !!s.root?._headerSearchOpen
      // TABLET_HEADER_SEARCH_QUERY (media.tabletS) is a bare max-width:1024px
      // — it still matches at a 390px phone. Excluding
      // MOBILE_HEADER_SEARCH_QUERY (media.mobileL, ≤768px) here is the
      // JS-side twin of the designSystem/media.js tabletSOnly CSS token:
      // this function writes `!important` INLINE styles, which outrank any
      // class (including the '@mobileL' one-deck overrides), so the
      // CSS-level tabletSOnly fix alone cannot stop this from also winning
      // at phone widths — this guard has to live here too (2026-08-02, "one
      // deck" mobile merge — live-verified this was pinning HeaderSearch's
      // tablet lane offsets on top of the merged deck).
      const tablet =
        !!window.matchMedia?.(this.TABLET_HEADER_SEARCH_QUERY).matches &&
        !window.matchMedia?.(this.MOBILE_HEADER_SEARCH_QUERY).matches

      this.clearHeaderSearchResponsiveStyle(el.node)
      if (!this.navAtTop(el)) return

      if (tablet) {
        // Same-name auto-extension can let HeaderSearch's base
        // left/top/transform beat the nested @tabletS props. Pin the
        // measured start-aligned lane inline, using the canonical
        // design-system media queries above.
        const applyTablet = () => {
          el.node.style.setProperty('left', this.TABLET_MIDDLE_LEFT, 'important')
          el.node.style.setProperty('right', 'auto', 'important')
          el.node.style.setProperty('top', 'var(--spacing-X2)', 'important')
          el.node.style.setProperty('max-width', this.TABLET_MIDDLE_MAX_WIDTH, 'important')
          el.node.style.setProperty('min-width', '0', 'important')
          el.node.style.setProperty('transform', open ? 'scale(1)' : 'scale(0.96)', 'important')
        }
        this.applyNowAndNextFrame(applyTablet)
        return
      }
    },

    syncHeaderTabsTabletLayout(headerNode) {
      if (typeof window === 'undefined' || !headerNode) return
      const node = headerNode.querySelector(':scope > [data-key="HeaderTabs"]')
      if (!node) return
      this.clearInlineStyles(node, ['left', 'right', 'top', 'width', 'max-width', 'transform'])
      // the top navbar's only (see navAtTop): the header names its place
      const place = headerNode.getAttribute && headerNode.getAttribute('data-nav-position')
      if (place && place !== 'top') return
      // Exclude mobileL — same reasoning as syncHeaderSearchResponsiveLayout
      // above (bare tabletS still matches at 390px, and this function's
      // `!important` inline styles would outrank the '@mobileL' one-deck
      // flex override otherwise; this was the actual root cause of
      // HeaderTabs rendering ~60px past the right edge of a 390px viewport
      // before this fix — the CSS-level tabletSOnly rename alone was not
      // enough).
      const tablet =
        !!window.matchMedia?.(this.TABLET_HEADER_SEARCH_QUERY).matches &&
        !window.matchMedia?.(this.MOBILE_HEADER_SEARCH_QUERY).matches
      if (!tablet) return

      const apply = () => {
        node.style.setProperty('left', this.TABLET_MIDDLE_LEFT, 'important')
        node.style.setProperty('right', 'auto', 'important')
        node.style.setProperty('top', 'var(--spacing-X2)', 'important')
        node.style.setProperty('width', 'fit-content', 'important')
        node.style.setProperty('max-width', this.TABLET_MIDDLE_MAX_WIDTH, 'important')
        node.style.setProperty('transform', 'none', 'important')
      }
      this.applyNowAndNextFrame(apply)
    },

    clearHeaderRightResponsiveStyle(node) {
      this.clearInlineStyles(node, [
        'position',
        'left',
        'right',
        'top',
        'margin-left',
        'max-width',
        'box-sizing',
        'background',
        'box-shadow',
        'padding',
        'min-height',
        'backdrop-filter',
        '-webkit-backdrop-filter'
      ])
    },

    syncHeaderRightResponsiveLayout(el) {
      if (typeof window === 'undefined' || !el?.node) return
      const mobile = window.matchMedia?.(this.MOBILE_HEADER_SEARCH_QUERY).matches
      // Exclude mobile — bare tabletS still matches at 390px, and this
      // branch's `!important` inline `position:fixed` used to win there
      // too, UNCONDITIONALLY pinning HeaderRight to the raw viewport corner
      // and taking it OUT of the "one deck" flex row entirely (2026-08-02
      // fix — this was the literal root cause of the reported "avatar
      // pinned hard against the right screen edge, no gutter" bug: it was
      // never a flex child on phone at all). The merged mobile deck now
      // handles HeaderRight's position/background purely via the static
      // '@mobileL' class below (flex:'0 0 auto' + the deck's own padding
      // for the gutter) — this function owns tabletS-and-wider only.
      const tablet = !!window.matchMedia?.(this.TABLET_HEADER_SEARCH_QUERY).matches && !mobile

      this.clearHeaderRightResponsiveStyle(el.node)
      if (!this.navAtTop(el)) return

      if (tablet) {
        // The vertical left rail makes the header taller, but HeaderRight
        // remains a top-row island. Pin it to the VISUAL viewport: canvas
        // routes can make the AppHeader's absolute containing block wider
        // than window.innerWidth, so an absolute `right` still places the
        // avatar beyond the visible right edge.
        const applyTablet = () => {
          el.node.style.setProperty('position', 'fixed', 'important')
          el.node.style.setProperty('left', 'auto', 'important')
          el.node.style.setProperty(
            'right',
            'calc(env(safe-area-inset-right) + var(--spacing-X2))',
            'important'
          )
          el.node.style.setProperty(
            'top',
            'calc(env(safe-area-inset-top) + var(--spacing-X2))',
            'important'
          )
          el.node.style.setProperty('margin-left', '0', 'important')
          el.node.style.setProperty(
            'max-width',
            'calc(100% - var(--hdr-left-w) - 2 * var(--spacing-X2) - var(--spacing-A))',
            'important'
          )
          el.node.style.setProperty('box-sizing', 'border-box', 'important')
        }
        this.applyNowAndNextFrame(applyTablet)
        return
      }

      if (mobile) {
        // Independent of the tablet branch above (mutually exclusive now —
        // see the `tablet` guard) — belt-and-suspenders backdrop-removal,
        // matching the static '@mobileL' class below via the SAME
        // imperative-override idiom every other responsive-sync in this
        // file uses, without any of the tablet branch's position/right/top
        // fixed-viewport pinning.
        const applyMobile = () => {
          el.node.style.setProperty('background', 'transparent', 'important')
          el.node.style.setProperty('box-shadow', 'none', 'important')
          el.node.style.setProperty('padding', '0', 'important')
          el.node.style.setProperty('min-height', '0', 'important')
          el.node.style.setProperty('backdrop-filter', 'none', 'important')
          el.node.style.setProperty('-webkit-backdrop-filter', 'none', 'important')
        }
        this.applyNowAndNextFrame(applyMobile)
      }
    }
  },
  // Shared floating-island chrome (Navbar + spacing/round). `theme` is
  // nulled and the background is transparent by default so the chrome
  // floats over canvas content — solid dark only while the home PageSlot
  // (AppWindow) is open, so it stays legible over app content.
  extends: 'IconNavbar',
  // Banner landmark — IconNavbar → brand Navbar is tag:'aside'; override the
  // tag to the semantic `<header>` so the persistent app header carries the
  // implicit `banner` role natively. The previous `attr: { role: 'banner' }`
  // kept the aside tag, and `banner` is not an allowed role on `<aside>` per
  // ARIA-in-HTML — axe `aria-allowed-role` (MARKETPLACE-AUDIT residue,
  // tickets/fable.md 2026-08-09). A top-level `<header>` (not nested in
  // main/section/article) IS the banner landmark, no explicit role needed.
  tag: 'header',
  // UI-NAVBAR-BLACK-GROUND-1 (Nika, option 2): the BLACK lives on the navbar
  // CONTAINER — this root spans the full strip (absolute, left/right pinned)
  // — while the islands stay unthemed (theme: null on HeaderLeft/HeaderRight,
  // UI-PROJECT-SWITCHER-GROUND-1). One continuous ground, no chip edges, no
  // gap. `navbar` theme = gray1.9+6 + blur dark / gray15.95 + blur light — a
  // token, never a raw colour. (558ea223c removed the transparent overrides
  // but THIS line was dropped by an aborted edit script — the write below is
  // the actual option-2 change.)
  //
  // ⚠️ REVERSED 2026-08-18 — Nika, verbatim: "black bg should not be on entire
  // navbar - only on islands". Option 2 (black on this container) is OUT; the
  // black moved to HeaderLeft/HeaderRight below. This root goes back to
  // theme: null AND regains the 2026-06-10 transparency pair, because that
  // pair is what keeps the bar from painting over an open PageSlot — removing
  // it was a side effect of the option-2 pass, not an intended change.
  theme: null,
  // Stay transparent even while a PageSlot / home AppWindow is open (2026-06-10
  // per Nika "remove header bg change when pageslot is open") — the HeaderLeft/
  // HeaderRight islands carry their own legible background over content.
  background: 'transparent',
  backdropFilter: 'none',
  flow: 'x',
  align: 'center',
  gap: 'A',
  padding: 'X2',
  round: '0',

  // Compact header font context (icons/chrome land at ≈12.5px). The Parcel
  // dev-server happens to emit this via a scratch token quirk — HeaderSearch's
  // `fontSize: '1.0625rem'` compiles to a `--font-size-Z` class that gets
  // hoisted onto the header root — but `smbls build` (prod) does NOT, so
  // dev.my/prod rendered the navbar a full step larger than localhost.
  // Declaring `fontSize: 'Z'` here (+ on HeaderLeft) makes the compact context
  // INTENTIONAL so it survives the production build and dev/prod match the
  // localhost size (Nika 2026-06-12 "keep the localhost sizes on dev + prod").
  // On the dev-server this is a no-op — the same `_fs-var--font-size-z` class
  // is already applied. See memory: scratch-devserver-vs-prod-token-mismatch.
  fontSize: 'Z',

  // The measured island widths — the onCreate ResizeObserver below writes
  // both on this node, inline, from the first frame it gets. These are only
  // the PRE-MEASURE values (the server render, the frame before the observer
  // publishes, a background tab with no animation frames), declared ONCE here
  // as spacing tokens so every consumer below — the centred-pill clamp and
  // the tablet lane offsets — reads one binding instead of its own raw px
  // fallback (brief §4.3 item 2, 2026-09-24). F / F2 are em-relative, so they
  // resolve in the consumer's font: in the search's 18.06px context F ≈
  // 200px and F2 ≈ 262px, where the old fallbacks were 210px and 270px; the
  // tablet lane's old 0px default slid the middle pill UNDER the left island
  // until the first measure (measured islands at 1920px: 162px and 252px).
  '--hdr-left-w': 'var(--spacing-F)',
  '--hdr-right-w': 'var(--spacing-F2)',

  // Overlay header (2026-06-06 per Nika): absolute over the page on every
  // surface — content flows underneath — rather than occupying a grid row.
  position: 'absolute',
  // iPad / WKWebView safe areas — inset the overlay off the status-bar notch and
  // (landscape) the camera housing. env() is 0 on non-notched devices, so this
  // is a no-op everywhere else. Paired with --shell-top's matching top inset in
  // workspace/app.js so page content still clears the now-lower header.
  top: 'env(safe-area-inset-top)',
  left: 'env(safe-area-inset-left)',
  right: 'env(safe-area-inset-right)',
  // Above every canvas element — including a dragged tile that briefly
  // raises itself to z=1000 in ProjectNode/IntegrationNode/etc.
  zIndex: '1100',

  // WHERE THE NAVBAR SITS (Nika 2026-10-24: "make sidebar optional to be
  // positioned at the top left right or bottom … left and right sidebars can
  // be toggle expandable"; functions/navbarLayout.js). The host frame places
  // the header — the workspace shell's overlay, the comm app's grid — and the
  // header turns to fit: on a RAIL (left or right) the three islands stack
  // into one column, the actions over the strip over the account. Each island
  // shares this root's `pill` fill (the skins rewrite that theme), painted by
  // the column's `:before` (the rail rule below). One surface covers the
  // flexible space above the account as well as the controls, without
  // stacking three blur layers. At the bottom the row stays a row.
  // `data-nav-position` is the tablet writers' gate (scope.navAtTop) and the
  // hosts' hook.
  //
  // THE RAIL IS AN ATTRIBUTE, NOT A CASE. Every element that turns on a rail
  // carries `data-nav-rail` ('narrow' | 'wide'; none off a rail) and its rail
  // rules select on it — CSS rules compounded onto the element's own class.
  // Never `isX` case blocks here: smbls 3.14.841 lands a case's keys as inline
  // styles, and this header's own layout writers (the tablet lanes, the
  // compact logo — scope above) clear inline properties by name on every
  // update, so a rail case was wiped wherever a writer clears the same key
  // (measured on dev 50e520dd0: the head stayed a 157px row, the search pill
  // sat mid-screen). A rule is never touched by `style.removeProperty`, and a
  // writer stands aside off the top (navAtTop), so nothing inline outranks
  // it there. The general `[data-nav-rail]` rule and its `="narrow"` /
  // `="wide"` rules never write the same key: they share one specificity, and
  // their order in the sheet must not decide.
  'data-nav-position': (el, s) => (el.call('navbarLayoutRead', s.root) || {}).position,
  'data-nav-rail': (el, s) => (el.call('navbarLayoutRead', s.root) || {}).railMode,
  //
  // THE RAIL'S GLASS IS ITS `:before`, NEVER THE COLUMN ITSELF (Nika
  // 2026-10-27: "fix navbar search button click on workspace for both left
  // and top navbar modes"). A `backdrop-filter` — the `pill` theme's blur —
  // makes its element the containing block of every `position: fixed`
  // descendant. The column carried it, so the search pill's "top centre of
  // the screen" (HeaderSearch's rail rule) resolved inside the column, and
  // the column's clip cut it away: a click on the rail's search opened the
  // search, focused its input and opened the launcher, and none of it showed
  // (measured on dev 2fc624ff0 at 1920px: the pill's `left: 50%` = 30px, its
  // box -313…382px under the logo, the launcher at the rail's corner). The
  // pseudo paints the same `pill` fill and blur under the islands, so the
  // rail still reads as one surface, and the column stays a plain box that
  // a fixed descendant passes through. `isolation` keeps the pseudo's
  // negative layer inside the column on every host (it adds no containing
  // block). Each island keeps the pill's ink from its own theme.
  '&[data-nav-rail]': {
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    gap: '0',
    padding: '0',
    round: 'radiusCard',
    overflow: 'hidden',
    isolation: 'isolate',
    ':before': {
      content: '""',
      position: 'absolute',
      inset: '0',
      zIndex: '-1',
      pointerEvents: 'none',
      borderRadius: 'inherit',
      theme: 'pill'
    }
  },
  // a wide (expanded) rail keeps one width whatever its names measure — the
  // page rail's own (AppSidebar `G`); a narrow rail is as wide as its glyphs
  '&[data-nav-rail="wide"]': { width: 'G' },
  '&[data-nav-rail="narrow"]': {
    width: 'D',
    '@coarse': { minWidth: 'calc(var(--size-controlH) + 2 * var(--spacing-Z))' }
  },
  // Was `background: 'transparent'` + `backdropFilter: 'none'` (2026-06-10
  // "remove header bg change when pageslot is open" — legibility then came
  // from the islands' own glass). The islands are unthemed now, so the
  // legible ground moved UP to this container: the `navbar` theme above
  // paints the whole strip continuously in both themes (UI-NAVBAR-BLACK-
  // GROUND-1, option 2). No transparent override left to fight it.
  // "One deck" merge (2026-08-02 per Nika "make navbars on mobile smarter,
  // more compact, modern … native-like"). Replaces three independently
  // floating glass islands (measured live at 390×844: HeaderLeft alone ran
  // 53.1px tall and 290px wide, HeaderTabs/HeaderSearch started inside that
  // same span — a physical overlap) with ONE full-width bar carrying the
  // ONLY backdrop-filter surface in the header: HeaderLeft/HeaderTabs/
  // HeaderRight go transparent at this breakpoint (their own '@mobileL'
  // blocks below) and this root paints the shared glass instead — merging
  // three compositor layers into one roughly halves the header's GPU
  // compositing cost on a phone, the actual performance story here.
  '@mobileL': {
    alignItems: 'center',
    gap: 'Z',
    // Full-bleed bar, not a floating inset pill (the OLD per-island look):
    // native top bars sit flush; content aligns to the SAME 'A' gutter
    // MainColumn's mobile padding uses (packages/workspace/pages/main.js
    // '@mobileL' padding: 'D1 A F A') via this padding's horizontal 'A', so
    // the deck's content and the page's content share one vertical grid
    // line. Longhand (not a 4-value shorthand) per the calc()/token
    // compiler gotcha — this one has no calc() to trip it, but every other
    // safe-area edge below does, so all four stay explicit for consistency.
    paddingTop: 'V',
    paddingBottom: 'V',
    paddingLeft: 'A',
    paddingRight: 'A',
    overflowX: 'clip',
    // THE TAP-TARGET TOKEN, not a rem gauge (2026-09-04 per Nika "on mobile
    // version, navbars and text are too small").
    //
    // This was `2.618rem`, with a comment reasoning "1.618² em at the 17px
    // root ≈ 44.5px … clears the 44pt tap-target guideline outright". The
    // root is NOT 17px on a phone. Measured live in a 390px viewport: the
    // document root computes 13px (16px at 1196px — the root is itself
    // responsive), so `2.618rem` landed 34.03px and the bar painted 38px
    // tall. Every rem gauge in this file inherited the same 24% shortfall,
    // and it is why the mobile deck read small.
    //
    // `controlH` is the house 44px control height from designSystem/sizes.js
    // — an absolute px token precisely because a tap target is a PHYSICAL
    // guideline, not a typographic one, so it must not scale with a root
    // that shrinks on the very devices the guideline exists for.
    minHeight: 'controlH',
    // The deck's own text steps up with the bar. `Z` computes 12.04px at the
    // phone's 13px root — under the 13px floor this shell uses for body text
    // everywhere else; `A` is exactly 1em (13px) in that context.
    fontSize: 'A',
    // The one shared glass surface (see the merge note above). Consumers
    // that want theme-driven canvas-card-glass everywhere else in this file
    // reference it unconditionally; nesting it here is the SAME token
    // resolution, just scoped to this breakpoint only — proven safe by the
    // `'@light'` nested inside `':hover'` blocks already shipping in
    // AppOrgSwitcher.js (nested selector wrapping is orthogonal to which
    // selectors are nested).
    // The skins' `pill` theme (see HeaderLeft): the deck is the header's one
    // pill on a phone.
    theme: 'pill',
    borderWidth: '0',
    // Slides fully off-screen on scroll-condense (see the
    // html[data-nav-condensed] rule in workspace/app.js) — transform-only,
    // so this is the one property that needs a transition; gated to this
    // breakpoint so desktop never pays for (or shows) a transform it never
    // sets. prefers-reduced-motion collapses this to an instant swap via the
    // existing global reduced-motion floor in app.js — no separate handling
    // needed here.
    transition: '240ms defaultBezier transform'
  },

  // Publish the LIVE left/right island widths as CSS vars on the header node.
  // The centered pill family (HeaderTabs / HeaderSearch) clamps its max-width
  // against them, so dead-centering — the design contract — can never slide
  // the pill under the islands when the header narrows: small screens, the
  // docked-assistant inset (the shell CSS shrinks this header by
  // --assistant-width), or a workspace navbar with many module tabs
  // (2026-07-27 per Nika "navbar looks jammed … add margin on middle section
  // so it does not stick"). Same measured-var pattern as the shell's
  // --shell-top / --prompt-clear ResizeObservers (workspace/app.js) — the
  // framework has no container queries, and pure CSS cannot express
  // "centered but clear of two variable-width siblings".
  onCreate: (el) => {
    // One document-level scroll-condense arm for the mobile deck (see
    // packages/shared/functions/navCondense.js) — idempotent, so mounting
    // AppHeader more than once in a session (a route remount) never attaches
    // a second listener. The phone-width query travels as an argument from
    // this element's own `scope:` (baked to a literal string at
    // serialization time) — navCondense.js must not import the brand
    // designSystem itself, see its header comment.
    el.call('armNavCondense', el.scope.MOBILE_HEADER_SEARCH_QUERY)
    if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') return
    if (el._islandRo) return
    const publish = () => {
      const node = el.node
      if (!node) return
      const left = node.querySelector(':scope > [data-key="HeaderLeft"]')
      const right = node.querySelector(':scope > [data-key="HeaderRight"]')
      const w = (n) => Math.ceil(n?.getBoundingClientRect?.().width || 0) + 'px'
      node.style.setProperty('--hdr-left-w', w(left))
      node.style.setProperty('--hdr-right-w', w(right))
      // Publish the tablet rail width to the shell root so page-owned floating
      // rails (TicketsSidebar, Studio AppSidebar, etc.) can reserve the same
      // measured lane instead of trying to solve overlap with z-index.
      // Vertical rail mode RETIRED (iPad, 2026-08-01 per Nika "fix navbar
      // consistency" — twice): the left island stays a horizontal pill at
      // every width, one island family. The measured --hdr-left-w above keeps
      // the tabs lane clear of it; page-owned floating rails fall back to
      // their base (non-lane) positioning now that the attr is never set.
      const doc = document.documentElement
      delete doc.dataset.headerLeftRail
      doc.style.removeProperty('--header-left-rail-w')
      el.scope.syncHeaderTabsTabletLayout(node)
    }
    const ro = new ResizeObserver(publish)
    const observe = () => {
      const node = el.node
      if (!node) return
      ro.observe(node)
      const left = node.querySelector(':scope > [data-key="HeaderLeft"]')
      const right = node.querySelector(':scope > [data-key="HeaderRight"]')
      if (left) ro.observe(left)
      if (right) ro.observe(right)
      publish()
    }
    el._islandRo = ro
    // Children mount after the header's own create — defer one frame so the
    // island nodes exist before the first observe/publish.
    window.requestAnimationFrame?.(observe) || setTimeout(observe, 0)
  },
  onRemove: (el) => {
    el._islandRo?.disconnect?.()
    el._islandRo = null
    if (typeof document !== 'undefined') {
      delete document.documentElement.dataset.headerLeftRail
      document.documentElement.style.removeProperty('--header-left-rail-w')
    }
  },

  // Auth-page hiding is owned by the shell mount (workspace/app.js AppHeader.hide
  // → AUTH_PATHS), the single source of truth. This component used to carry its
  // OWN narrow hide (/login + /signin only) which, taking precedence over the
  // shell's broader gate, let the header flash on /signup, /reset-password,
  // /verify-email, etc. Removed so the shell's full AUTH_PATHS set governs.

  // Left glass island — Logo + chrome action icons grouped into ONE
  // canvas-card-glass navbar so the header reads as themed islands like the
  // other navbars (2026-06-08 per Nika: "put Logo and HeaderActions in a
  // navbar that unifies it; theme canvas-card-glass as other navbars").
  HeaderLeft: {
    extends: 'IconNavbar',
    // Plain `<div>`, not IconNavbar's default `<aside>`: this island is a
    // visual control cluster INSIDE the banner, not complementary page
    // content. Two unlabeled `<aside>`s (this + HeaderRight) exposed two
    // identical `complementary` landmarks — axe `landmark-unique`
    // (MARKETPLACE-AUDIT residue, tickets/fable.md 2026-08-09). Landmarks
    // are for top-level page regions; chrome sub-groups stay generic.
    tag: 'div',
    // UI-PROJECT-SWITCHER-GROUND-1 (Nika 2026-08-18, verbatim: "removes gray
    // background from project switcher and keeps it consistant so no empty
    // black right area"): NO glass on this island. The org/project switcher's
    // "grey chip" WAS this island's canvas-card-glass fill (measured live:
    // rgba(4,4,4,.85) dark / rgba(255,255,255,.95) light) — the buttons
    // themselves are `tertiary` = transparent at rest. The AppHeader bar is
    // transparent by design, so with the island fill gone the whole strip
    // resolves to the ONE page ground (the stage) — no chip, no gap, and no
    // second painted ground to drift at the next theme change. Hover/active
    // washes on the controls stay. Applied to BOTH desktop islands (stripping
    // one just moves the seam); the @mobileL merged deck keeps its glass (the
    // condensed header's legibility layer — mobile is already ONE surface).
    // `theme: null` — not merely dropping the glass: the IconNavbar base this
    // island extends carries `theme: 'navbar'` (gray1.9+6 dark — measured
    // rgba(10,10,10,.9) live after the first cut), so an absent theme falls
    // back to a THIRD ground. Null clears the inherited theme outright.
    // ⚠️ REVERSED 2026-08-18 (Nika: "only on islands"): the black lives HERE
    // now, not on the container. `navbar` is the token the IconNavbar base
    // already carried — restoring it, NOT the canvas-card-glass that was the
    // grey chip Nika removed in UI-PROJECT-SWITCHER-GROUND-1. Glass stays off.
    // THE PILL (2026-09-26, the two skins — HOME_AMBIENT_SKIN_SPEC §1,
    // DESIGN-WL-SKINS-AMBIENT-DEFAULT-SOLID-WHITELABEL-1): every bar of the
    // header row carries the brand `pill` theme. In the solid skin it IS the
    // `navbar` recipe (pillSolid under a 35px blur); the ambient skin rewrites
    // it to the card glass at the root. Its own name because menus and
    // toolbars also carry `navbar`, and those stay opaque in both skins.
    theme: 'pill',
    // No border (match the search pill), and grow to the search's height. The
    // search is position:absolute (out of the flex flow), so the islands can't
    // auto-stretch to it — pin minHeight to the search height (~38.5px). rem
    // (root = 17px) keeps it scope-independent, like the search's fontSize.
    borderWidth: '0',
    // Pin the compact em context so mounting or updating UnreadBadge cannot
    // make this island inherit the larger application font size.
    fontSize: '0.735rem',
    minHeight: HEADER_ISLAND_MIN_HEIGHT,
    maxHeight: '20rem',
    // THE CAPSULE NEVER CUTS ITS CONTENT (overflow law §3b.1 — a plate clips
    // for its radius only, and this pill's background is rounded by its own
    // border-radius). A clip here cut the top of the bell's unread badge
    // ("21", Nika 2026-09-28) — the badge floats off the bell's corner, the
    // house placement it shares with the chat icon's. Nothing else needs it:
    // the compact phone state hides the actions itself, and the dropdowns
    // render in the shell-root layer, not inside this island.
    overflow: 'visible',
    subscribeTo: ['_canvasSlot', '_canvasSlotSecond'],
    // ON A RAIL (the root's note — attribute rules, never cases: this island's
    // compact writer clears its inline layout on every update): the head of
    // the one column — the logo, the rail's toggle, the actions. Narrow, they
    // stack; wide (expanded), the logo and the toggle share the first row and
    // the actions run under them.
    'data-nav-rail': (el, s) => (el.call('navbarLayoutRead', s.root) || {}).railMode,
    '&[data-nav-rail]': {
      background: 'transparent',
      backdropFilter: 'none',
      gap: 'Z',
      padding: 'Z',
      paddingBottom: '0',
      maxHeight: 'none',
      round: '0',
      fontSize: 'inherit',
      flexShrink: '0',
      // IN FLOW (the note on HeaderRight's own rail block): a row's sticky
      // recipe buys nothing in a column and costs a layer over the tab list.
      position: 'relative'
    },
    '&[data-nav-rail="narrow"]': { flexDirection: 'column' },
    '&[data-nav-rail="wide"]': { flexWrap: 'wrap', justifyContent: 'space-between' },
    onCreate: (el, s) => {
      el.scope.syncHeaderLeftCompactLayout(el, s)
      el.scope.watchMediaQueries(
        el,
        '_headerLeftMedia',
        [el.scope.MOBILE_HEADER_SEARCH_QUERY],
        () => el.scope.syncHeaderLeftCompactLayout(el, s)
      )
    },
    onUpdate: (el, s) => el.scope.syncHeaderLeftCompactLayout(el, s),
    onRemove: (el) => {
      el.scope.clearHeaderLeftCompactStyle(el?.node)
      if (typeof document !== 'undefined') {
        delete document.documentElement.dataset.headerLeftCompact
      }
      el.scope.unwatchMediaQueries(el, '_headerLeftMedia')
    },
    // Slightly roomier horizontal padding (2026-06-09 per Nika "increase
    // padding x in HeaderLeft") — keep IconNavbar's vertical 'V', bump the
    // horizontal 'Z' → 'Z1' so the icon row has more side breathing room.
    padding: 'V Z1',
    // Tablet: stay a HORIZONTAL pill — the vertical-rail mode is retired
    // (iPad, 2026-08-01 per Nika "fix navbar consistency" — the rail read as
    // a different system next to the horizontal pill family). Compact the
    // paddings/gaps like mobile so the island cedes lane width to the tabs
    // pill, whose measured-var clamp + overflow chevron own the tight-lane
    // problem.
    //
    // tabletSOnly (not tabletS): this block's `padding` disagrees with
    // '@mobileL' below on the SAME property, and a bare max-width tabletS
    // (≤1024px) still matches at 390px too — the atomic CSS engine's global
    // class-insertion order, not source order, would then decide the winner
    // (verified live: it does NOT reliably favor the narrower breakpoint).
    // tabletSOnly (1024–769px) never overlaps '@mobileL' (≤768px), so this
    // can never win a tie it shouldn't. See designSystem/media.js.
    '@tabletSOnly': {
      padding: 'V Z',
      gap: 'Z'
    },
    // "One deck" leading zone (2026-08-02) — the shared glass now lives on
    // the AppHeader root, so this island goes transparent; overflow must
    // open up too, or the AppOrgSwitcher mount's own tapSlop ':after' (below)
    // gets clipped at this box's edge instead of growing past it.
    '@mobileL': {
      background: 'transparent',
      backdropFilter: 'none',
      boxShadow: 'none',
      padding: '0',
      minHeight: '0',
      maxHeight: 'none',
      overflow: 'visible',
      flex: '0 0 auto',
      // Release the `fontSize: '0.735rem'` pin above, for the same reason the
      // root's minHeight moved off rem: at the phone's 13px root that pin
      // computes 9.55px, and it is what made the org-switcher label measure
      // 9.28px on a 390px screen (2026-09-04 per Nika "navbars and text are
      // too small"). The pin exists to stop UnreadBadge inflating this
      // island's em context — a token does that job just as well, and this
      // one tracks the deck instead of a root that is not there.
      fontSize: 'Z'
    },

    Logo: {
      extends: 'Link',
      'data-nav-rail': (el, s) => (el.call('navbarLayoutRead', s.root) || {}).railMode,
      '&[data-nav-rail]': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'controlHCompact',
        height: 'controlHCompact',
        minWidth: 'controlHCompact',
        minHeight: 'controlHCompact',
        padding: '0',
        boxSizing: 'border-box',
        '@coarse': {
          width: 'controlH',
          height: 'controlH',
          minWidth: 'controlH',
          minHeight: 'controlH'
        }
      },
      href: '/',
      aria: { label: (el) => el.call('polyglot', 'shell.header.logo.ariaLabel') },
      cursor: 'pointer',
      color: 'title',
      padding: 'Y',
      align: 'center center',
      // A finger's target — the 44pt FLOOR (_touchTarget.js): the home logo
      // measured 18 x 18 at 834px and 21 x 21 at 1032+ (dev DFI, touch
      // simulated, DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1).
      '@coarse': { position: 'relative', ...touchFloor },
      // Hidden in the merged mobile deck — redundant with the leading-zone
      // switcher below AND with HeaderTabs' MobileTabs label strip, whose
      // FIRST entry is the same home destination this logo links to.
      '@mobileL': { display: 'none' },
      // `extends: 'Icon'` so the glyph actually renders — a bare `icon` prop
      // on a plain div drew an empty box (the old Logo was hide-gated for
      // members, so this path never drew the logo before the flatten).
      LogoIcon: {
        extends: 'Icon',
        name: 'logov2',
        // Header icon size, tuned live with Nika (2026-06-09): settled on the
        // base 'A' (≈1em ≈12.5px in the header's compact font context). Kept
        // explicit so every HeaderLeft glyph stays in lockstep.
        boxSize: 'A A',
        aspectRatio: '1 / 1'
      }
    },

    // THE RAIL'S TOGGLE (functions/navbarLayout.js navbarRailToggle; Nika
    // 2026-10-24: "left and right sidebars can be toggle expandable"): the
    // names beside the glyphs, or the glyphs alone — on a rail only. The glyph
    // is a window with its column on the rail's own side.
    NavRailToggle: {
      extends: ['CompactIconButton', 'NavbarTooltipTrigger'],
      hide: (el, s) => !(el.call('navbarLayoutRead', s.root) || {}).rail,
      color: 'title',
      title: (el, s) =>
        (el.call('navbarLayoutRead', s.root) || {}).expanded
          ? el.call('polyglot', 'shell.navbar.collapse')
          : el.call('polyglot', 'shell.navbar.expand'),
      'aria-label': (el, s) =>
        (el.call('navbarLayoutRead', s.root) || {}).expanded
          ? el.call('polyglot', 'shell.navbar.collapse')
          : el.call('polyglot', 'shell.navbar.expand'),
      'aria-expanded': (el, s) =>
        (el.call('navbarLayoutRead', s.root) || {}).expanded ? 'true' : 'false',
      Icon: {
        name: (el, s) =>
          (el.call('navbarLayoutRead', s.root) || {}).position === 'right'
            ? 'lnSidebarRight'
            : 'lnSidebarLeft',
        boxSize: 'A A'
      },
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('navbarRailToggle')
      }
    },

    // Keep the primary destinations directly below the rail toggle, in
    // DOM order as well as visual order. Their bar/phone mounts stay hidden
    // here; every mount reuses the same action and focus behavior.
    HeaderHomeBtn: {
      hide: (el, s) => !(el.call('navbarLayoutRead', s.root) || {}).rail
    },
    SearchBtn: {
      hide: (el, s) => !(el.call('navbarLayoutRead', s.root) || {}).rail
    },

    // Left action cluster — the chrome icons grouped in ONE flat flex, right
    // after the logo (2026-06-08 per Nika: "icons to left, org switcher to
    // the right"). Membership-gated items keep their own hide gates.
    HeaderActions: {
      // Rail actions form one column; expanded rows add the localized name
      // beside each glyph, below the logo and collapse control.
      'data-nav-rail': (el, s) => (el.call('navbarLayoutRead', s.root) || {}).railMode,
      // Separate communication from Home/Search and the app list, using
      // equal insets on both sides of each boundary.
      '&[data-nav-rail]': {
        borderTop: 'hairline solid line',
        borderBottom: 'hairline solid line',
        paddingTop: 'Z',
        paddingBottom: 'Z'
      },
      '&[data-nav-rail="narrow"]': { flexDirection: 'column', gap: 'Z' },
      '&[data-nav-rail="wide"]': {
        flexBasis: '100%',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 'Z'
      },
      flow: 'x',
      align: 'center',
      gap: 'A',
      // Theme-aware chrome icons. `childProps` pushes the color onto each
      // button so it wins over their own color, and the icons inherit it via
      // `fill: currentColor`.
      color: 'title',
      childProps: { color: 'title' },
      maxHeight: '20rem',
      // Hidden in the merged mobile deck (2026-08-02) — four action icons +
      // the leading switcher + the center nav strip + search + avatar do not
      // fit one compact ~44px row on a 390px phone without recreating the
      // exact overlap/jam this redesign exists to fix. See the ship report:
      // this is a deliberate, flagged scope cut, not an oversight.
      '@mobileL': {
        display: 'none'
      },

      // (TicketsButton removed 2026-06-09 per Nika — the Tickets canvas tab in
      // the centered HeaderTabs bar covers this jump now.)

      Messenger: {
        extends: ['Messenger', 'NavbarRailControl'],
        hide: (el) => !checkActiveMember(el),
        // The tooltip plugin reads this at SHOW time: nothing to show while
        // the chat drawer is open, so "Direct messages" never paints over the
        // Chats panel it just opened (live on dev 2026-09-26 — the bell's
        // fix, the same contract; DESIGN_SPEC §5 Tooltip).
        tooltipText: (el, s) =>
          s.root?._chatDrawerOpen || (el.call('navbarLayoutRead', s.root) || {}).expanded
            ? ''
            : el.call('polyglot', 'shell.messenger.tooltip'),
        // Match the HeaderLeft icon size (kept in lockstep, 2026-06-09).
        // Active (drawer-open) highlight is rendered by IconButton, which
        // honors the `active:` prop Messenger declares (2026-06-14).
        Icon: { boxSize: 'A A' }
      },
      // Mail — the built-in email client (/mail), sitting between the
      // messenger and the call button so the three "reach a person" chrome
      // icons read as one group. Dark by default: the `mail` feature flag
      // ships `defaultOn: false` (functions/featureFlagsData.js), so this
      // button stays hidden until an admin turns Mail on at /flags.
      //
      // The gate is `navItemVisible(root, '/mail')`, the same route→flag
      // resolver every navigation surface uses (ROUTE_FLAGS maps '/mail' →
      // 'mail'), never a hand-rolled flag read — one resolver, no drift.
      // `subscribeTo` is required, not decorative: a root-state read inside
      // a factory does NOT auto-subscribe the element, so without it the
      // flag flip at /flags would not re-run this `hide` until a reload
      // (same pair AppNavbar and CanvasViewNav subscribe to).
      Mail: {
        // The house tooltip, fed by `title` (TitleTooltipTrigger keeps the native one off).
        extends: ['IconButton', 'NavbarRailControl'],
        subscribeTo: ['_featureFlags', 'workspace', '_mailDrawerOpen'],
        hide: (el, s) => {
          if (!checkActiveMember(el)) return true
          return !navItemVisible(s && s.root, '/mail')
        },
        title: (el) => el.call('polyglot', 'shell.header.mail.tooltip'),
        // Nothing to show while its drawer is open (same contract as the
        // bell and the messenger).
        tooltipText: (el, s) =>
          s.root?._mailDrawerOpen || (el.call('navbarLayoutRead', s.root) || {}).expanded
            ? ''
            : el.call('polyglot', 'shell.header.mail.tooltip'),
        aria: { label: (el) => el.call('polyglot', 'shell.header.mail.tooltip') },
        // Open-state highlight while the mail drawer is open — the Messenger
        // button's contract (Messenger.js + _activeHighlight.js): the class
        // factory reads the signal itself so it stays reactive.
        ...activeHighlight,
        class: { isActive: (el, s) => !!(s && s.root && s.root._mailDrawerOpen) },
        active: (el, s) => !!(s && s.root && s.root._mailDrawerOpen),
        // `name` (not `icon`) — required, not stylistic. See the NewCall
        // comment below and components/headerIconGate.test.js: IconText's
        // `Icon.if` gate never reads the child's own `el.icon`, so an
        // `icon:` key here renders an EMPTY button.
        Icon: { name: 'email', boxSize: 'A A' },
        // 2026-09-05 per Nika "mail should open sidebar just like chats": the
        // button toggles the MailOverlay — the same left glass float the
        // Messenger opens — never the /mail page. Same choreography as
        // Messenger.js: fold the lazily-bundled mail context FIRST (so the
        // shell's MailOverlaySlot can mount), then flip the overlay key through
        // headerOverlayPatch so the other header overlays close. When the mail
        // package is not registered (an older shell, a test root), fall back
        // to the /mail page in the PageSlot rather than a dead click.
        onClick: async (e, el, s) => {
          e.stopPropagation()
          const root = s && s.root
          const fns = el && el.context && el.context.functions
          if (!root || !fns || typeof fns.ensureMailContext !== 'function') {
            el.call('openAppPage', '/mail', { preferSlot: true, split: e.metaKey || e.ctrlKey })
            return
          }
          const opening = !root._mailDrawerOpen
          if (opening) {
            const okFold = await el.call('ensureMailContext')
            if (okFold === false) return
            if (root.notificationsPanelOpen) el.call('closeDropdown')
          }
          root.update(headerOverlayPatch('mail', opening))
        }
      },
      // Start a call from anywhere (2026-06-12 per Nika "add call icon in
      // top left navbar") — opens the meet New-call modal; the modal's
      // Start routes to /meet and joins. Hidden where the meet functions
      // aren't registered (no-backend apps) or for non-members.
      NewCall: {
        // The house tooltip, fed by `title` (TitleTooltipTrigger keeps the native one off).
        extends: ['IconButton', 'NavbarRailControl'],
        hide: (el) => {
          if (!checkActiveMember(el)) return true
          return typeof el?.context?.functions?.openNewRoomModal !== 'function'
        },
        title: (el) => el.call('polyglot', 'shell.header.newCall.tooltip'),
        aria: { label: (el) => el.call('polyglot', 'shell.header.newCall.tooltip') },
        // `name` (not `icon`) — required, not stylistic. IconText.Icon.if
        // (smbls default-config components/Icon/index.js) never reads the
        // child's own `el.icon`, only `el.name` (or `parent.icon`), so this
        // rendered an empty button after smbls `2e4ba032e` removed the
        // SquareButton `icon:'smile'` placeholder that had been masking the
        // wrong key (tickets/opus.md P1, 2026-08-09). Same shape as
        // HomeBtn/SearchBtn/ArrowBtn (HeaderTabs.js).
        Icon: { name: 'video', boxSize: 'A A' },
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('openNewRoomModal')
        }
      },
      NotificationWrapper: {
        hide: (el) => !checkActiveMember(el),
        position: 'relative',
        color: 'title',
        // NotificationBell sets its own muted color, so the group's title
        // doesn't reach it via inheritance — pin it explicitly. Icon bumped
        // to match the HeaderLeft icon size (kept in lockstep, 2026-06-09).
        // Active (panel-open) highlight is rendered by IconButton via the
        // `active:` prop NotificationBell declares (2026-06-14).
        NotificationBell: {
          extends: ['NotificationBell', 'NavbarRailControl'],
          '&[data-nav-rail="wide"] > [data-key="UnreadBadge"]': {
            position: 'static',
            marginLeft: 'auto',
            order: '2',
            flexShrink: '0'
          },
          color: 'title',
          tooltipText: (el, s) =>
            s.root?.notificationsPanelOpen || (el.call('navbarLayoutRead', s.root) || {}).expanded
              ? ''
              : el.call('polyglot', 'shell.notificationBell.tooltip'),
          Icon: { boxSize: 'A A' }
        }
        // Panel renders through the shell-root dropdown layer now (the bell
        // calls openDropdown) — no in-header panel to position.
      }
      // AppAssistantToggle (the magicstar icon) is unmounted from this
      // cluster (2026-09-02 per Nika "remove magic icon from top left
      // navbar"). The assistant keeps three entry points: the composer's
      // OpenSidebarBtn (expanded state), the ReasoningPill island, and the
      // minimized-dock handle — the header icon duplicated all of them.
      // The component itself stays exported for other hosts.
    },

    // Leading-zone identity affordance — "one deck" @mobileL merge
    // (2026-08-02 per Nika): "the workspace/identity affordance (the
    // existing switcher entry point, compacted)". A SECOND AppOrgSwitcher
    // mount, not a relocation of HeaderRight's — its Trigger anchors the
    // dropdown to itself (`anchorEl: el`), so a second instance is safe and
    // self-contained; _prefetchOrgWorkspaces' module-level in-flight guard
    // makes the doubled onCreate poll a harmless no-op. Desktop untouched
    // (hidden there); on mobile it replaces Logo/HeaderActions above — Home
    // is still one tap away as MobileTabs' leading entry (HeaderTabs.js), so
    // nothing here is a lost destination.
    //
    // NOTE for the demo-lockdown reader: app.js neutralizes THIS instance
    // too (mirroring the HeaderRight.AppOrgSwitcher override there) — see
    // the matching `HeaderLeft: { AppOrgSwitcher: {...} }` block in
    // workspace/app.js's AppHeader mount.
    AppOrgSwitcher: {
      display: 'none',
      '@mobileL': { display: 'flex' },
      Trigger: {
        padding: 'Y1 Z1',
        // Coarse tap-slop — Trigger extends brand `Button`, which (unlike
        // NavbarButton/IconButton) carries none of its own.
        '@coarse': {
          position: 'relative',
          ':after': { content: '""', position: 'absolute', inset: 'tapSlop' }
        },
        Label: {
          // Tightened 2026-08-02 (from 'F') per the same "give the strip the
          // center it was designed to own" pass that moved Search out of
          // HeaderTabs — the org name only needs to be RECOGNIZABLE, not
          // fully spelled out, and every px it claims here is a px the
          // MobileTabs label strip doesn't get (HeaderTabs is flex:1, so it
          // absorbs whatever the leading/trailing zones don't).
          //
          // …but B1 overshot: it clipped "Natali" to a bare "N…", and a
          // workspace switcher that cannot name its workspace has lost the
          // one job the leading zone exists for (measured live at 390px).
          // C1 recognizes a typical name while still yielding most of what
          // the 'F' original was hoarding.
          maxWidth: 'C1',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          // The overflow law (HOME_AMBIENT_SKIN_SPEC §3b rule 8): a name cut
          // to fit a phone header names itself in full on hover.
          title: (el, s) =>
            el.scope.activeOrg(s.root)?.name || el.call('polyglot', 'shell.orgSwitcher.orgLabel')
        }
      }
      // The invite button STAYS on the phone (Nika 2026-10-08, the invite
      // redesign): HeaderRight's instance is display:none at @mobileL, so
      // this copy is the only way to invite from a phone. It used to say
      // `hide: true` here; the shell's own mount override (workspace/app.js,
      // the demo-lockdown `InviteBtn.hide`) replaced it, so phones showed the
      // button anyway — this file now says what ships.
    }
  },

  // Centered search pill — same chrome + footprint (width H3) as the
  // bottom CanvasPromptTextarea (per Nika). Absolute + translate keeps it
  // dead-centered regardless of the left/right cluster widths; the
  // canvas-card-glass theme and no border make search and prompt read as
  // one family of AI-adjacent inputs.
  //
  // FOCUS IS THE GLOBAL RING (brief §4.3, 2026-09-24). The pill used to paint
  // its own blue two-ring glow plus a lift shadow on `:focus-within`; a
  // component never re-implements focus. The input inside is the focusable
  // control and fills the pill (radiusPill, 1px short of each edge), so the
  // brand reset's `input:focus-visible` ring (shadow token `focusRing`,
  // specificity 0,1,1) lands on the pill's own outline — the input's base
  // `boxShadow: 'none'` is a single class (0,1,0) and never outranks it
  // (measured on the served stylesheet). The pill FLOATS over live content,
  // so it keeps the `float` level its theme already paints at rest; focus
  // is a state, not a new elevation, so nothing lifts.
  HeaderSearch: {
    // Morphs in/out (crossfade + subtle scale) with HeaderTabs instead of an
    // instant hide (2026-06-14 per Nika "add morphing animation when search is
    // clicked"). Driven by the reactive `.isSearchOpen` class — a reactive
    // opacity/transform factory freezes in v3.14, whereas the class toggles
    // reliably and CSS transitions its static values. Closed = faded + scaled
    // down + non-interactive (kept in the DOM so the transition can run).
    isSearchOpen: (el, s) => !!s.root?._headerSearchOpen,
    // `_slotSplitIntent` too: its arrival while the search is open re-focuses
    // the input (focusSearchOnOpen), and onUpdate must run to see it.
    subscribeTo: [
      '_headerSearchOpen',
      '_slotSplitIntent',
      // the navbar's place: the tablet writer re-runs, and stands aside off the top
      '_homeLook',
      'userPrefs',
      '_isPhoneViewport'
    ],
    // ON A RAIL (the root's note — an attribute rule: the tablet writer
    // clears this pill's inline top / left / width on every update) the pill
    // opens at the top centre of the screen — a rail column is too narrow to
    // hold it, and the strip it would cover stays usable. Fixed, so the rail's
    // rounded clip never cuts it. That holds only while no ancestor is a
    // containing block for a fixed box, so the column paints its glass on a
    // `:before` (the root's rail rule, locked by
    // __tests__/navbarSearchOpensOnEveryNavbar.test.js). Its four transforms
    // read variables (base, `.isSearchOpen`, both tablet blocks below — each
    // falls back to its own value as before), so this rule moves the pill
    // without writing the key the inline `.isSearchOpen` writes.
    'data-nav-rail': (el, s) => (el.call('navbarLayoutRead', s.root) || {}).railMode,
    '&[data-nav-rail]': {
      position: 'fixed',
      top: 'Z1',
      left: '50%',
      right: 'auto',
      width: 'H3',
      maxWidth: 'calc(100vw - 2 * var(--spacing-C))',
      '--hs-closed': 'translateX(-50%) scale(0.96)',
      '--hs-open': 'translateX(-50%) scale(1)',
      '--hs-closed-tab': 'translateX(-50%) scale(0.96)',
      '--hs-open-tab': 'translateX(-50%) scale(1)'
    },
    scope: {
      // A11Y-1 (2026-08-15): focus-on-open + focus-restore-on-close for the
      // search morph. Same edge-detected-transition / bounded-poll / return-
      // focus idiom as ContextDropdown.js's `focusMenuOnOpen` (Modal.js and
      // AppDialog's open/close focus-lifecycle ship the same `_wasOpen`
      // shape) — NOT requestAnimationFrame, same reasoning as there: Chrome
      // throttles rAF in backgrounded/automated tabs, and this element is
      // still `opacity:0`/mid-220ms-transition on the frame `.isSearchOpen`
      // first flips, so a single rAF tick can still measure a pre-layout
      // 0×0 box. The bounded poll instead waits for a real
      // offsetWidth/offsetHeight before focusing.
      //
      // Closing (Escape, the X button, the search button's second press, or
      // a press outside — closeSearchOnOutsidePress below) runs through ONE
      // state flip whichever path fired: HeaderSearch.js's Input.onKeydown
      // Escape handler and ClearSearchMorph set `_headerSearchOpen: false`,
      // and the other two reach the same writes through the shared
      // `closeHeaderSearch` — so restoring focus HERE, on the false-edge,
      // covers every dismiss path without duplicating the restore call at
      // each one. `__headerSearchReturnFocusTo` is stashed on root by
      // SearchBtn's own handler (HeaderTabs.js) — NOT `document.activeElement`
      // captured here, because that handler calls `e.preventDefault()`, so
      // the button never actually receives default browser focus to read
      // back. An outside press clears it first: focus follows that press.
      // Where focus returns when a ⌘K-opened search closes and nothing held
      // focus before it (HeaderSearch.onDocumentKeydown asks): the visible
      // SearchBtn that owns this pill — the HeaderTabs copy on desktop and
      // tablet, the HeaderRight copy on a phone.
      // THE OPEN SEARCH KEEPS THE KEYBOARD (ledger WH-5 — sam, keyboard only:
      // with Ctrl+K open, Tab left the panel for the hidden buttons of the bar
      // behind it, and the first result tile took ~21 presses). Between the
      // input and the Close X the browser's own order stands; at either edge
      // the keyboard goes to the open launcher (the workspace's
      // focusSearchLauncher: its first row forward, its last row back — the
      // launcher keeps Tab inside itself from there), else the two wrap. The
      // morph's two controls by their DOMQL references, never a node query (a
      // hidden results row must not move an edge). Returns whether it moved
      // focus.
      keepSearchTab(e, el, s) {
        if (!e || e.key !== 'Tab' || e.defaultPrevented) return false
        if (!(s && s.root && s.root._headerSearchOpen)) return false
        const own = [el.Form?.Input?.node, el.ClearSearchMorph?.node].filter(
          (n) => n && typeof n.focus === 'function'
        )
        if (!own.length) return false
        const first = own[0]
        const last = own[own.length - 1]
        const doc = el.node?.ownerDocument || (typeof document === 'undefined' ? null : document)
        const active = doc && doc.activeElement
        if (active !== (e.shiftKey ? first : last)) return false
        e.preventDefault()
        const handed =
          typeof el?.context?.functions?.focusSearchLauncher === 'function' &&
          !!el.call('focusSearchLauncher', e.shiftKey ? 'last' : 'first')
        if (!handed) (e.shiftKey ? last : first).focus()
        return true
      },
      searchReturnTarget(el) {
        const header = el && el.parent
        const candidates = [
          header?.HeaderLeft?.SearchBtn?.node,
          header?.HeaderTabs?.SearchBtn?.node,
          header?.HeaderRight?.SearchBtn?.node
        ]
        return candidates.find((n) => n && n.offsetParent !== null) || null
      },
      // A PRESS OUTSIDE THE OPEN SEARCH ENDS IT (`onDocumentMousedown` below
      // — the note there). A press that belongs to the search leaves it
      // open: the pill; the visible search button, whose press toggles (the
      // trigger's own job, as the dropdown plugin leaves its anchor's press
      // to the trigger); the launcher's own panel and any other panel of the
      // dropdown host; the context menu's panel (a launcher tile's or a
      // result row's menu — its row acts on the click that follows); and a
      // dialog, the phone navigation or a phone rail, which own every press
      // while they are up.
      //
      // BY PLACE, NOT BY STATE, for the context menu: it lays a backdrop over
      // the whole screen and shuts on that backdrop's POINTERDOWN, which the
      // browser fires before this mousedown, so its state is gone by now. A
      // press on that backdrop dismisses the menu AND the search: the
      // launcher ends with the menu (focus lands on the page), and a pill
      // left open behind it is the half-closed search N2 removed (measured
      // live on dev 2026-10-28, the launcher tile's menu). A closed host takes
      // no press: the context menu is hidden, the dropdown and dialog hosts
      // are `pointer-events: none` with nothing open in them. All by DOMQL
      // reference and `contains`, never a node query. Returns whether it
      // closed.
      closeSearchOnOutsidePress(ev, el, s) {
        const root = s && s.root
        const target = ev && ev.target
        if (!root || !root._headerSearchOpen || !target || !el || !el.node) return false
        if (el.node.contains(target)) return false
        const trigger = this.searchReturnTarget(el)
        if (trigger && trigger.contains(target)) return false
        const layer =
          typeof el.context?.functions?.shellOverlayOpen === 'function'
            ? el.call('shellOverlayOpen')
            : null
        if (['modalRoot', 'phoneNav', 'mobileRail'].includes(layer)) return false
        const shell = typeof el.getRoot === 'function' ? el.getRoot() : null
        const owners = [shell?.DropdownRoot, shell?.ContextDropdown?.Menu, shell?.ModalRoot]
        if (owners.some((owner) => owner?.node && owner.node.contains(target))) return false
        return !!el.call('closeHeaderSearch', { returnFocusTo: null })
      },
      focusSearchOnOpen(el, s) {
        if (typeof window === 'undefined' || !el) return
        const isOpen = !!s.root?._headerSearchOpen
        // A split intent that ARRIVES while the search is already open (a
        // pane's split button asks the open search for the next pick) is an
        // open request too: the input takes focus again. The intent's own
        // edge, never a close-then-open of the search to fake an open edge.
        const intent = !!s.root?._slotSplitIntent
        const intentArrived = isOpen && intent && !el._wasSplitIntent
        el._wasSplitIntent = intent
        if (isOpen === el._wasSearchOpen && !intentArrived) return
        el._wasSearchOpen = isOpen
        if (!isOpen) {
          const returnTo = s.root?.__headerSearchReturnFocusTo
          if (returnTo && typeof returnTo.focus === 'function') returnTo.focus()
          if (s.root) s.root.__headerSearchReturnFocusTo = null
          return
        }
        el.__searchFocusGen = (el.__searchFocusGen || 0) + 1
        const gen = el.__searchFocusGen
        let ticks = 0
        const tick = () => {
          if (gen !== el.__searchFocusGen || !el.node) return
          if (el.node.offsetWidth > 0 && el.node.offsetHeight > 0) {
            const input = el.Form?.Input?.node
            if (input && typeof input.focus === 'function') input.focus()
            return
          }
          if (++ticks < 10) window.setTimeout(tick, 20)
        }
        tick()
      }
    },
    opacity: '0',
    pointerEvents: 'none',
    // CLOSED IS GONE FOR THE KEYBOARD AND THE SCREEN READER TOO (ledger WH-5:
    // the opacity-hidden input and "Close search" were Tab stops on every
    // page). The morph leaves the accessibility tree and its two controls the
    // Tab order (their own `tabindex` below) — not `inert` / `visibility:
    // hidden`: the phone's SearchBtn focuses the input INSIDE its tap (iOS
    // raises the keyboard for nothing else) right after it writes
    // `_headerSearchOpen`, and a reactive attribute lands one microtask later,
    // so an inert input would refuse that focus. tabindex -1 keeps script
    // focus, and both attributes are gone before the task ends.
    ariaHidden: (el, s) => (s.root?._headerSearchOpen ? null : 'true'),
    onKeydown: (e, el, s) => el.scope.keepSearchTab(e, el, s),
    // AN OUTSIDE PRESS ENDS THE SEARCH, ON EVERY NAVBAR (N2, Nika 2026-10-27).
    // The focus note above always named this close, and it never existed:
    // the field's blur (HeaderSearch.js Input.onBlur) ends only the results
    // and the launcher (`closeNavSearch`), never `_headerSearchOpen`. So a
    // press on the page closed the launcher and left the pill open with
    // focus on <body> — measured live on dev 6d3da4967, on a top bar and on
    // a left rail. The dropdown plugin's own outside-press contract: a
    // capture-phase document mousedown that lets the press through, so the
    // pill and its launcher end on the SAME press and the press still lands
    // where it was aimed (a tab, the composer, a page control). What counts
    // as outside: scope.closeSearchOnOutsidePress. Focus follows the press;
    // Escape, the X and the button's own toggle still hand it back to the
    // button. FA503: declared, so DOMQL registers it once and removes it in
    // dispose().
    onDocumentMousedown: {
      capture: true,
      handler: (ev, el, s) => el.scope.closeSearchOnOutsidePress(ev, el, s)
    },
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'var(--hs-closed, translate(-50%, -50%) scale(0.96))',
    width: 'H3',
    // Clamp the centered pill so it can never overlap the measured islands
    // (see the header's island-width observer above): the symmetric 2×max()
    // keeps it dead-centered while guaranteeing an --spacing-A clearance on
    // BOTH sides; `width: H3` still caps the natural size. Fallbacks cover
    // the pre-measure frame.
    maxWidth: 'calc(100% - 2 * (max(var(--hdr-left-w), var(--hdr-right-w)) + var(--spacing-A)))',
    minWidth: '0',
    // The em-based H3 width token resolves against the element's font-size,
    // so the search must sit in the SAME font context as the bottom
    // CanvasPromptTextarea (font-size `A` = 18.0625px) to land on the same
    // 725px. The `A` token can't be used here — `--font-size-a` is undefined
    // in the absolute header's DOM scope (font tokens live on the app root) —
    // so pin the equivalent in scope-independent rem: 18.0625 / 17 = 1.0625.
    fontSize: '1.0625rem',
    // ONE ROW, FOUR BARS. The same gauge HeaderLeft/HeaderTabs/HeaderRight
    // read above and below — the search used to size itself purely from its
    // input and rendered 41px against their 38px (measured live at 1920px,
    // 2026-09-20). With the variant's own padding step (HeaderSearch.js) the
    // input is 37.80px, so this gauge is what the row finally measures, and
    // it can never drift from the islands again.
    minHeight: HEADER_ISLAND_MIN_HEIGHT,
    // The header's pill (the skins' `pill` theme — see HeaderLeft): the same
    // surface as the islands and the tab strip in both skins.
    theme: 'pill',
    borderWidth: '0',
    onCreate: (el, s) => {
      el.scope.syncHeaderSearchResponsiveLayout(el, s)
      el.scope.watchMediaQueries(
        el,
        '_headerSearchMedia',
        [el.scope.MOBILE_HEADER_SEARCH_QUERY, el.scope.TABLET_HEADER_SEARCH_QUERY],
        () => el.scope.syncHeaderSearchResponsiveLayout(el, s)
      )
      el.scope.focusSearchOnOpen(el, s)
    },
    onUpdate: (el, s) => {
      el.scope.syncHeaderSearchResponsiveLayout(el, s)
      el.scope.focusSearchOnOpen(el, s)
    },
    onRemove: (el) => {
      el.scope.clearHeaderSearchResponsiveStyle(el?.node)
      el.scope.unwatchMediaQueries(el, '_headerSearchMedia')
    },
    transition: 'opacity 220ms defaultBezier, transform 220ms defaultBezier',
    '.isSearchOpen': {
      opacity: '1',
      pointerEvents: 'auto',
      // a rail opens the pill beside itself, unshifted (`--hs-open`, the
      // rail block below); the top and the bottom centre it as ever
      transform: 'var(--hs-open, translate(-50%, -50%) scale(1))',
      // The open pill stacks OVER the header islands (HeaderLeft / HeaderRight
      // extend the brand Navbar: sticky, z 9). It used to lift only while its
      // input held focus (the dropdown parent's `:focus-within` z 1000), so
      // an open pill whose input had not taken focus yet — a phone tap, where
      // the focus came from a timer — or had handed it to the launcher sat
      // UNDER them: the org name, the search button and the avatar drawn over
      // "Search across everything…" (measured on dev DFI at 375 / 393 / 440,
      // DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1).
      zIndex: '10',
      '@tabletS': {
        transform: 'var(--hs-open-tab, scale(1))'
      },
      '@mobileL': {
        left: 'A',
        right: 'A',
        width: 'auto',
        maxWidth: 'none',
        // Centred in the phone deck. A shell that pins the open pill under
        // the screen's top edge itself (the workspace phone dock, its
        // `phoneNavigationStyles`) sets `--hs-open-phone: none`. A variable,
        // because the framework emits this block `!important` (the pill also
        // writes its open keys inline), so a shell rule on `transform` itself
        // never wins: measured on dev 2fc624ff0 at 390px, the open pill kept
        // this -50% shift under the dock's `transform: none`, and its top
        // 7.6px sat above the screen.
        transform: 'var(--hs-open-phone, translateY(-50%) scale(1))'
      }
    },
    '@tabletS': {
      // Start inside the measured lane between the left island and
      // HeaderRight. Keep the search's normal width, clamping it to whatever
      // space remains instead of stretching and centering it across the lane.
      left: 'calc(var(--hdr-left-w) + var(--spacing-X2) + var(--spacing-A))',
      right: 'auto',
      top: 'X2',
      width: 'H3',
      maxWidth:
        'calc(100% - var(--hdr-left-w) - var(--hdr-right-w) - 2 * (var(--spacing-X2) + var(--spacing-A)))',
      transform: 'var(--hs-closed-tab, scale(0.96))'
    },
    '@mobileL': {
      left: '50%',
      right: 'auto',
      top: '50%',
      // NO scale on a phone (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1): the open
      // pill's own transform lands inline and the tablet-lane cleanup above
      // strips inline transform on every update (it must — a phone turning
      // out of the tablet band takes the lane's inline transform with it),
      // and the open state's nested phone block did not re-attach on the
      // live flip: the OPEN phone pill read this closed scale(0.96), centred
      // (measured on dev 60c343516 at 375 / 393 / 440). Unscaled here, the
      // phone morph is the opacity crossfade alone and the open pill is full
      // size whichever path lands.
      transform: 'translate(-50%, -50%)',
      width: 'calc(100dvw - var(--spacing-C))',
      maxWidth: 'calc(100dvw - var(--spacing-C))',
      minWidth: '0'
    },
    Form: {
      '@mobileL': {
        width: '100%',
        minWidth: '0',
        maxWidth: '100%'
      },
      Input: {
        background: 'transparent',
        // out of the Tab order while the morph is closed (WH-5, see above)
        tabindex: (el, s) => (s.root?._headerSearchOpen ? null : '-1'),
        '@mobileL': {
          width: '100%',
          minWidth: '0',
          maxWidth: '100%'
        }
      },
      // The ⌘K caps stop a `Y` step before the Close X (it replaces the
      // chevron here): the X's inset `A` plus its `controlHCompact` box.
      // Sizes come from the tokens, so the caps can never slide under the X
      // at any root size.
      Shortcut: {
        right: 'calc(A + controlHCompact + Y)'
      }
    },
    // Hide the decorative apps chevron in the morph; show a clickable X that
    // resets the search back to the tab bar (item 1 per Nika "active Search
    // should have X so it gets back to initial state").
    ArrowDown: { hide: true },
    // THE house close X (brand CloseButton — F2, one X for every panel).
    ClearSearchMorph: {
      extends: 'CloseButton',
      position: 'absolute',
      right: 'A',
      top: '50%',
      transform: 'translateY(-50%)',
      'aria-label': (el) => el.call('polyglot', 'shell.headerSearch.close'),
      // out of the Tab order while the morph is closed (WH-5, see above)
      tabindex: (el, s) => (s.root?._headerSearchOpen ? null : '-1'),
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        const input = el.parent?.node?.querySelector('input')
        if (input) input.value = ''
        try {
          el.call('closeNavSearch')
        } catch (err) {
          console.warn('[AppHeader] closeNavSearch unavailable:', err)
        }
        s.root.update({ _headerSearchOpen: false, _navSearchOpen: false })
      }
    }
  },

  // Segmented canvas-nav — the default centered header element, swapped to
  // HeaderSearch when the search morph opens (2026-06-09 per Nika). The
  // component (HeaderTabs.js) owns its own centered+absolute anchor.
  HeaderTabs: {
    // Crossfades out as HeaderSearch morphs in (opacity only — HeaderTabs owns
    // its own centering transform, so don't clobber it). 2026-06-14 per Nika.
    // Not on a rail: the pill opens at the top of the screen there, clear of
    // the strip, which stays usable.
    isSearchOpen: (el, s) =>
      !!s.root?._headerSearchOpen && !(el.call('navbarLayoutRead', s.root) || {}).rail,
    // ON A RAIL (the root's note — attribute rules: the tablet writer clears
    // this island's inline left / top / width / transform) the strip is the
    // middle of the one column: in the flow (not the centred pill), the tabs
    // stacked; narrow, centred on the glyphs, wide, as wide as the rail.
    'data-nav-rail': (el, s) => (el.call('navbarLayoutRead', s.root) || {}).railMode,
    '&[data-nav-rail]': {
      position: 'relative',
      background: 'transparent',
      backdropFilter: 'none',
      left: 'auto',
      top: 'auto',
      transform: 'none',
      width: 'auto',
      maxWidth: 'none',
      flex: '0 1 auto',
      minHeight: '0',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      gap: 'Z',
      padding: 'Z',
      paddingBottom: '0',
      round: '0'
    },
    '&[data-nav-rail="wide"]': { alignItems: 'stretch' },
    // Share the sibling islands' height so the three read as one row. Set here
    // rather than inside HeaderTabs.js so all three heights stay derivable from
    // the one HEADER_ISLAND_MIN_HEIGHT above — HeaderTabs.js owns its own
    // centering/anchor, this only pins vertical geometry.
    minHeight: HEADER_ISLAND_MIN_HEIGHT,
    alignItems: 'center',
    opacity: '1',
    pointerEvents: 'auto',
    transition: 'opacity 200ms defaultBezier',
    '.isSearchOpen': { opacity: '0', pointerEvents: 'none' },
    // tabletSOnly (not tabletS): this block's left/right/top/width/maxWidth/
    // transform all disagree with '@mobileL' below on the SAME properties —
    // exactly the live-verified cascade-tie bug (see designSystem/media.js
    // tabletSOnly's own comment): a bare max-width tabletS still matches at
    // 390px, and the atomic engine's global class-insertion order (not which
    // block is "more specific") decided the winner, so HeaderTabs rendered
    // 60px off the right edge of the viewport with the mobileL block
    // silently losing every one of these properties. tabletSOnly (1024–
    // 769px) can never overlap '@mobileL' (≤768px).
    '@tabletSOnly': {
      left: 'calc(var(--hdr-left-w) + var(--spacing-X2) + var(--spacing-A))',
      right: 'auto',
      top: 'X2',
      width: 'fit-content',
      maxWidth:
        'calc(100% - var(--hdr-left-w) - var(--hdr-right-w) - 2 * (var(--spacing-X2) + var(--spacing-A)))',
      transform: 'none'
    },
    // "One deck" center zone (2026-08-02) — HeaderTabs stops being an
    // absolutely-centered pill clamped between the islands' measured widths
    // and becomes a normal flex participant sitting BETWEEN HeaderLeft and
    // HeaderRight in the deck's own row (the measured --hdr-left-w/
    // --hdr-right-w clamp above is a tabletSOnly-and-wider concern only — at
    // this breakpoint the deck's own flex layout does the job instead). Its
    // own glass goes transparent: the shared surface now lives on the
    // AppHeader root (see the merge note there). HeaderTabs.js's own
    // '@mobileL' block (gap/padding) still applies underneath this one.
    '@mobileL': {
      position: 'relative',
      left: 'auto',
      right: 'auto',
      top: 'auto',
      transform: 'none',
      flex: '1 1 0%',
      minWidth: '0',
      width: 'auto',
      maxWidth: 'none',
      height: '100%',
      minHeight: '0',
      background: 'transparent',
      backdropFilter: 'none',
      boxShadow: 'none',
      borderWidth: '0'
    }
  },

  // Right glass island — org switcher, presence, and the avatar menu grouped
  // into ONE canvas-card-glass navbar, pushed to the right edge via
  // marginLeft:auto (2026-06-08 per Nika: "same for AppOrgSwitcher and
  // MeWrapper"). marginLeft moved off AppOrgSwitcher onto this wrapper.
  HeaderRight: {
    extends: 'IconNavbar',
    // Plain `<div>` — same landmark-unique reasoning as HeaderLeft above.
    tag: 'div',
    // UI-PROJECT-SWITCHER-GROUND-1 (Nika 2026-08-18, verbatim: "removes gray
    // background from project switcher and keeps it consistant so no empty
    // black right area"): NO glass on this island. The org/project switcher's
    // "grey chip" WAS this island's canvas-card-glass fill (measured live:
    // rgba(4,4,4,.85) dark / rgba(255,255,255,.95) light) — the buttons
    // themselves are `tertiary` = transparent at rest. The AppHeader bar is
    // transparent by design, so with the island fill gone the whole strip
    // resolves to the ONE page ground (the stage) — no chip, no gap, and no
    // second painted ground to drift at the next theme change. Hover/active
    // washes on the controls stay. Applied to BOTH desktop islands (stripping
    // one just moves the seam); the @mobileL merged deck keeps its glass (the
    // condensed header's legibility layer — mobile is already ONE surface).
    // `theme: null` — not merely dropping the glass: the IconNavbar base this
    // island extends carries `theme: 'navbar'` (gray1.9+6 dark — measured
    // rgba(10,10,10,.9) live after the first cut), so an absent theme falls
    // back to a THIRD ground. Null clears the inherited theme outright.
    // ⚠️ REVERSED 2026-08-18 (Nika: "only on islands"): the black lives HERE
    // now, not on the container. `navbar` is the token the IconNavbar base
    // already carried — restoring it, NOT the canvas-card-glass that was the
    // grey chip Nika removed in UI-PROJECT-SWITCHER-GROUND-1. Glass stays off.
    // THE PILL (2026-09-26, the two skins — HOME_AMBIENT_SKIN_SPEC §1,
    // DESIGN-WL-SKINS-AMBIENT-DEFAULT-SOLID-WHITELABEL-1): every bar of the
    // header row carries the brand `pill` theme. In the solid skin it IS the
    // `navbar` recipe (pillSolid under a 35px blur); the ambient skin rewrites
    // it to the card glass at the root. Its own name because menus and
    // toolbars also carry `navbar`, and those stay opaque in both skins.
    theme: 'pill',
    marginLeft: 'auto',
    // Match HeaderLeft: no border + same minHeight as the search pill.
    borderWidth: '0',
    minHeight: HEADER_ISLAND_MIN_HEIGHT,
    // Roomier spacing between the right-cluster items — presence avatars, org
    // switcher, account menu (2026-06-10 per Nika "increase gap between things
    // in orgswitcher navbar").
    gap: 'A',
    // The avatar sits as far from the pill's right edge as from its top and
    // bottom (Nika 2026-09-24, verbatim: "reduce space on avatar navbar at
    // top right so avatar has same space at right as top and bottom").
    // Measured live at 1920px: top 5.11px, bottom 5.11px, right 18.00px —
    // the trailing edge carried the island's horizontal Z (8.76px) where the
    // top carries V (1.28px), plus MeWrapper's own marginRight Y (5.41px).
    // The trailing padding now IS the vertical one (V); the square,
    // centred MeWrapper adds the same slack on every side, so right = top =
    // bottom. The leading edge keeps Z for the org switcher.
    padding: 'V V V Z',
    // tabletMOnly (not tabletM): this block's gap/padding disagree with
    // '@mobileL' below on the SAME properties — the live-verified
    // cascade-tie bug (see designSystem/media.js tabletSOnly's comment for
    // the full mechanism). tabletMOnly (1280–769px) never overlaps
    // '@mobileL' (≤768px).
    '@tabletMOnly': {
      // Compact one step before the vertical-rail breakpoint so the org island
      // and the middle tabs keep a visible gutter around ~1050px.
      gap: 'Z',
      padding: 'V V V Z',
      fontSize: 'Y'
    },
    // the navbar's place: the tablet writer (onUpdate) re-runs, and stands
    // aside off the top (scope.navAtTop)
    subscribeTo: ['_homeLook', 'userPrefs', '_isPhoneViewport'],
    // ON A RAIL (the root's note — attribute rules: the tablet writer clears
    // this island's inline padding / min-height / margin) the foot of the one
    // column: the switcher and the account, stacked; wide, the switcher's
    // name runs full width and the avatar keeps to the start (MeWrapper).
    'data-nav-rail': (el, s) => (el.call('navbarLayoutRead', s.root) || {}).railMode,
    '&[data-nav-rail]': {
      flexDirection: 'column',
      background: 'transparent',
      backdropFilter: 'none',
      marginLeft: '0',
      marginTop: 'auto',
      minHeight: '0',
      gap: 'Z',
      padding: 'Z',
      round: '0',
      fontSize: 'inherit',
      flexShrink: '0',
      // IN FLOW, never a sticky layer over the destinations
      // (DESIGN-SHELL-RAIL-TRAILING-CLUSTER-COVERS-TABS-1). This island
      // extends the brand Navbar — `position: sticky`, `top: 0`, `z-index: 9`,
      // a ROW's recipe. The rail is a column that does not scroll, so sticky
      // buys nothing here and costs a painted layer over the tab list:
      // `elementFromPoint` on the tail of that list answered `HeaderRight` and
      // its `Icon` at 1194 x 834 (the parent card's live matrix), and a
      // focus() into a tab the lane clips scrolls this `overflow: hidden`
      // rail, which would slide a sticky island down across the tabs. The
      // tabs' own room is the lane's (HeaderTabs.Tabs scrolls what it cannot
      // show); this island only keeps its size and its place at the foot.
      position: 'relative'
    },
    '&[data-nav-rail="wide"]': { alignItems: 'stretch' },
    onCreate: (el) => {
      el.scope.syncHeaderRightResponsiveLayout(el)
      el.scope.watchMediaQueries(
        el,
        '_headerRightMedia',
        [el.scope.MOBILE_HEADER_SEARCH_QUERY, el.scope.TABLET_HEADER_SEARCH_QUERY],
        () => el.scope.syncHeaderRightResponsiveLayout(el)
      )
    },
    onUpdate: (el) => el.scope.syncHeaderRightResponsiveLayout(el),
    onRemove: (el) => {
      el.scope.clearHeaderRightResponsiveStyle(el?.node)
      el.scope.unwatchMediaQueries(el, '_headerRightMedia')
    },
    '@mobileL': {
      // Online members and org switcher are hidden here, so avoid rendering a
      // second glass island behind the standalone avatar trigger — the
      // shared surface now lives on the AppHeader root (the "one deck"
      // merge). backdropFilter listed explicitly for completeness alongside
      // background/boxShadow: scope.syncHeaderRightResponsiveLayout already clears
      // it imperatively on every real phone (mobileL implies its `tablet`
      // gate), this static rule just makes the class-level contract match.
      gap: '0',
      padding: '0',
      minHeight: '0',
      maxWidth: 'none',
      background: 'transparent',
      backdropFilter: 'none',
      boxShadow: 'none',
      // Trailing edge, A-line gutter from the viewport edge (never flush) —
      // AppHeader root's own '@mobileL' paddingRight: 'A' already provides
      // it; this island's own padding stays 0 so it doesn't double up.
      flex: '0 0 auto'
    },

    // Presence avatars moved OUT of the header to the fixed bottom-left dock
    // (2026-07-25 per Nika "move this to bottom left corner") — see
    // OnlineMembersDock in workspace/components/OnlineMembers.js, mounted at
    // the shell root in workspace/app.js.
    // Org-name label matched to the tab bar (item 4 per Nika — the org
    // switcher looked smaller). The 'A' token resolves per font-context
    // (~12.5px here vs ~15px in HeaderTabs), so pin a scope-independent rem
    // on BOTH (here + HeaderTab) so they render identically.
    AppOrgSwitcher: {
      order: '2',
      '@mobileL': { display: 'none' },
      // a narrow rail stacks the switcher over the invite; a wide one keeps
      // them in a row, the invite at the far end (the root's note: attribute
      // rules)
      'data-nav-rail': (el, s) => (el.call('navbarLayoutRead', s.root) || {}).railMode,
      '&[data-nav-rail="narrow"]': { flexDirection: 'column', gap: 'Z' },
      '&[data-nav-rail="wide"]': { justifyContent: 'space-between' },
      Trigger: {
        // a narrow rail has no room for the name: a square with its monogram
        // (Label below), the chevron off
        'data-nav-rail': (el, s) => (el.call('navbarLayoutRead', s.root) || {}).railMode,
        '&[data-nav-rail="narrow"]': {
          minWidth: 'controlHCompact',
          padding: '0',
          justifyContent: 'center'
        },
        Icon: {
          hide: (el, s) => (el.call('navbarLayoutRead', s.root) || {}).railMode === 'narrow'
        },
        Label: {
          // the name, or on a narrow rail its monogram (functions/
          // navbarLayout.js navbarRailLabel — the one rule, which the
          // workspace shell's own label override calls too)
          text: (el, s) =>
            el.call(
              'navbarRailLabel',
              el.scope.activeOrg(s.root)?.name || el.call('polyglot', 'shell.orgSwitcher.orgLabel'),
              s.root
            ),
          'data-nav-rail': (el, s) => (el.call('navbarLayoutRead', s.root) || {}).railMode,
          '&[data-nav-rail="narrow"]': { fontWeight: '700' },
          fontSize: '0.9rem',
          // A TABLET HEADER IS FOR THE TABS (DESIGN-SHELL-RESPONSIVE-IOS-
          // SAFARI-1). The centred tab island is clamped symmetrically to the
          // WIDER side island (`maxWidth: 100% - 2 × max(left, right)`), so a
          // long workspace name on this side starved the tabs: on dev DFI
          // ("Decentralized Fleet Intelligence - Flintelligence", ~330px) the
          // tabs read "Validators Inci…" at 1032px and 834px while ~200px sat
          // empty left of them. At ≤1280 the name is cut to `F` with an
          // ellipsis and names itself in full on hover (the overflow law, as
          // the phone header's copy above); the switcher menu lists it whole.
          '@tabletM': {
            fontSize: '0.78rem',
            maxWidth: 'F',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          },
          title: (el, s) =>
            el.scope.activeOrg(s.root)?.name || el.call('polyglot', 'shell.orgSwitcher.orgLabel')
        }
      }
    },

    // Trailing-zone search — @mobileL "one deck" merge (2026-08-02, revised
    // same day per a live-probe finding: SearchBtn competing with MobileTabs
    // for HeaderTabs' width was capping the label strip at 80px of 212px
    // available). Bare-key auto-extend from the top-level SearchBtn export
    // (HeaderTabs.js) — desktop/tablet keep the OTHER mount, in HeaderTabs
    // (hidden here at those widths); this one is mobile-only, sitting beside
    // the avatar per the brief's own "Trailing zone: search + avatar".
    SearchBtn: {
      display: 'none',
      '@mobileL': { display: 'flex', order: '50' }
    },

    MeWrapper: {
      extends: 'AppControl',
      compact: true,
      tag: 'button',
      type: 'button',
      ariaLabel: (el) => el.call('polyglot', 'shell.accountMenu.open'),
      ariaHaspopup: 'menu',
      // `controlHCompact`, not `controlH`, and MEASURED: every other control
      // in this trailing group renders 36px, so a 44px avatar wrapper made
      // the whole right-hand bar 47px against 38px for the leading zone and
      // the tab strip — it sat 4px proud at BOTH ends of a 56px header
      // (live, 1920px, 2026-09-20). The tap-target guideline is not lost
      // here: on a coarse pointer the enlarged area comes from the `:after`
      // pseudo below, whose `inset: tapSlop` is what actually receives the
      // finger, and @coarse restores the full `controlH` box as well.
      minWidth: 'controlHCompact',
      minHeight: 'controlHCompact',
      padding: '0',
      // No margin of its own: the trailing air is the pill's trailing padding
      // (HeaderRight, above), equal to its vertical padding (Nika 2026-09-24).
      // The 2026-09-20 `marginRight: 'Y'` stacked 5.41px on top of the
      // island's padding and left the avatar 18px from the edge against 5px
      // above and below. Never a top padding here either — it de-centres the
      // avatar inside this `align: center center` wrapper.
      borderWidth: '0',
      background: 'transparent',
      color: 'title',
      round: 'radiusPill',
      align: 'center center',
      // Keep the avatar last in the flex row regardless of which membership-
      // gated siblings render before it.
      order: '99',

      // The account wrapper occupies its whole touch target. Its avatar
      // is visual content; neither needs a hit area over adjacent controls.
      position: 'relative',
      '@coarse': {
        // The full 44px box comes back for a finger. It lives HERE and not
        // beside the base `minHeight` above because a second `'@coarse'` key
        // in this same object would silently win — last key defined, not
        // merged — and the base would look applied while doing nothing.
        minWidth: 'controlH',
        minHeight: 'controlH',
        position: 'relative',
        ':after': { display: 'none' }
      },
      cursor: 'pointer',
      // a wide rail stretches the column's rows; the avatar keeps its square
      // at the start, as the tabs and the switcher's name do (the root's note)
      'data-nav-rail': (el, s) => (el.call('navbarLayoutRead', s.root) || {}).railMode,
      '&[data-nav-rail="wide"]': {
        width: '100%',
        justifyContent: 'flex-start',
        gap: 'Z',
        padding: '0 Z',
        height: 'controlHCompact',
        round: 'radiusControl'
      },
      ':hover': { background: 'hover' },
      ':active': { background: 'selected' },
      ':focus-visible': { shadow: 'focusRingInset' },
      AccountLabel: {
        tag: 'span',
        hide: (el, s) => !(el.call('navbarLayoutRead', s.root) || {}).expanded,
        text: '{{ shell.accountMenu.open | polyglot }}',
        order: '1',
        fontSize: 'A',
        minWidth: '0',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      },
      onClick: (e, el) => el.call('openAccountMenu', el.lookdown('Me') || el),

      // Avatar trigger — opens the account menu through the shared
      // @symbo.ls/dropdown plugin (migrated 2026-06-10 from the legacy
      // hand-rolled `root.showMeMenu` opacity/pointerEvents overlay; the
      // plugin owns placement, outside-click dismiss and ESC). Menu
      // content is built HERE, in the opener, with `root` captured —
      // content rendered in the dropdown portal doesn't inherit page
      // state reliably (cf. AppOrgSwitcher / team-and-agents
      // dropdown-panel-content-reactivity), so every row acts through
      // the captured `root`, never its own `s.root`.
      Me: {
        extends: 'Avatar',
        avatarType: 'initials',
        boxSize: 'B1',
        cursor: 'pointer',
        position: 'relative',
        // The parent supplies the full touch target.
        '@coarse': {
          position: 'relative',
          ':after': { display: 'none' }
        },
        ariaHidden: true,
        // Decorative: the MeWrapper button carries the name
        // (`shell.accountMenu.open`). Without an own `alt` the framework
        // Avatar names the image after its element key, so a Georgian
        // session announced the English word "Me". `''` wins there
        // (`el.alt != null`), and an empty alt is the decorative image.
        alt: '',
        src: (el, s) => el.call('avatarUrlFor', s.root?.currentUserEmail),
        ':hover': { filter: 'brightness(1.15)' },
        onClick: (e, el) => {
          e.stopPropagation()
          // Open the shared house ContextDropdown (the same dropdown the /files
          // context menu uses) so the account menu reuses its EXACT shape +
          // styles (MenuPanel / MenuPanelItem). Anchored under the avatar,
          // right-aligned; the rows + dispatch live in accountMenuItems /
          // accountMenuAction (shared/functions/accountMenu.js), and the open
          // itself is `openAccountMenu` there — shared with MeWrapper, which
          // owns the coarse-pointer tap area (see its comment above).
          el.call('openAccountMenu', el)
        }
      }
    }
  }
}

// Back-compat alias — the old `AppHeaderWithIcons` (full header with the
// FirstApps/SecondApps icon rows) was unused outside this module and is
// folded into the flat AppHeader. Kept as an alias so any stray import
// resolves; mount `MainNavbar` directly if the icon rows are needed.
export const AppHeaderWithIcons = AppHeader

export const AppNavLink = {
  extends: 'Link',
  cursor: 'pointer',
  padding: 'X1 A',
  round: 'radiusPill',
  textDecoration: 'none',
  transition: 'all 200ms ease',
  color: 'caption',
  fontSize: 'Z2',
  fontWeight: '500',
  href: (el, s) => s.href,
  // The rail rows carry bundle KEYS (workspace/components/IntranetAppHeader.js
  // NAV_LINKS). That producer already resolves them, but a second consumer
  // that does not would ship a raw dotted key to the screen, so the render
  // site resolves too — `translate()` is a no-op on an already-resolved string
  // and on a tenant-supplied label alike.
  text: (el, s) => el.call('polyglot', s.label || ''),
  background: (el, s) => (s.root.currentPath === s.href ? 'blue.2' : 'transparent'),
  ':hover': { background: 'hover' }
}
